import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import type { MutableRefObject } from "react";

export type DistanceCullingPosition = readonly [number, number, number] | THREE.Vector3;

export type UseDistanceCullingOptions = {
  targetRef: MutableRefObject<any>;
  objectRef?: MutableRefObject<THREE.Object3D | null>;
  position?: DistanceCullingPosition;
  enterDistance?: number;
  exitDistance?: number;
  updateInterval?: number;
  enabled?: boolean;
  initialVisible?: boolean;
};

export type DistanceCullingResult = {
  visible: boolean;
  distance: number | null;
};

const DEFAULT_ENTER_DISTANCE = 35;
const DEFAULT_EXIT_DISTANCE = 45;
const MIN_INTERVAL = 0;

export function useDistanceCulling({
  targetRef,
  objectRef,
  position,
  enterDistance,
  exitDistance,
  updateInterval = 0.3,
  enabled = true,
  initialVisible,
}: UseDistanceCullingOptions): DistanceCullingResult {
  const resolvedInitialVisible = useMemo(() => {
    if (!enabled) return true;
    if (typeof initialVisible === "boolean") return initialVisible;
    return false;
  }, [enabled, initialVisible]);

  const [visible, setVisible] = useState(resolvedInitialVisible);
  const visibleRef = useRef(resolvedInitialVisible);
  const distanceRef = useRef<number | null>(null);
  const frameTimerRef = useRef(0);
  const targetPosition = useMemo(() => new THREE.Vector3(), []);
  const objectPosition = useMemo(() => new THREE.Vector3(), []);

  const positionKey = useMemo(() => {
    if (position instanceof THREE.Vector3) {
      return `${position.x}|${position.y}|${position.z}`;
    }
    if (Array.isArray(position) && position.length === 3) {
      return `${position[0]}|${position[1]}|${position[2]}`;
    }
    return "__missing_position__";
  }, [position]);

  const { enter, exit } = useMemo(() => {
    const safeEnter = Math.max(0, enterDistance ?? DEFAULT_ENTER_DISTANCE);
    const suggestedExit = exitDistance ?? DEFAULT_EXIT_DISTANCE;
    const safeExit = Math.max(suggestedExit, safeEnter + Math.max(1, safeEnter * 0.1));
    return { enter: safeEnter, exit: safeExit };
  }, [enterDistance, exitDistance]);

  const evaluateVisibility = useCallback(() => {
    if (!targetRef?.current) return;

    const target = targetRef.current.group ?? targetRef.current;
    if (!target) return;

    let translation: any = null;
    if (typeof target.translation === "function") {
      translation = target.translation();
    } else if (target.translation) {
      translation = target.translation;
    } else if (target.position) {
      translation = target.position;
    }

    if (!translation) return;

    targetPosition.set(translation.x ?? 0, translation.y ?? 0, translation.z ?? 0);

    if (objectRef?.current) {
      objectRef.current.getWorldPosition(objectPosition);
    } else if (position instanceof THREE.Vector3) {
      objectPosition.copy(position);
    } else if (Array.isArray(position) && position.length === 3) {
      objectPosition.set(position[0] ?? 0, position[1] ?? 0, position[2] ?? 0);
    } else {
      return;
    }

    const distanceSq = targetPosition.distanceToSquared(objectPosition);
    distanceRef.current = Math.sqrt(distanceSq);

    const enterSq = enter * enter;
    const exitSq = exit * exit;

    const nextVisible = visibleRef.current
      ? distanceSq <= exitSq
      : distanceSq <= enterSq;

    if (nextVisible !== visibleRef.current) {
      visibleRef.current = nextVisible;
      setVisible(nextVisible);
    }
  }, [enter, exit, objectPosition, objectRef, position, targetPosition, targetRef]);

  useEffect(() => {
    if (enabled) return;
    if (!visibleRef.current) {
      visibleRef.current = true;
      setVisible(true);
    }
  }, [enabled]);

  useEffect(() => {
    visibleRef.current = resolvedInitialVisible;
    setVisible(resolvedInitialVisible);
  }, [resolvedInitialVisible]);

  useEffect(() => {
    if (!enabled) return;
    evaluateVisibility();
  }, [enabled, evaluateVisibility, positionKey]);

  useFrame((_, delta) => {
    if (!enabled) return;

    const interval = Math.max(MIN_INTERVAL, updateInterval);
    frameTimerRef.current += delta;
    if (frameTimerRef.current < interval) return;

    frameTimerRef.current = 0;
    evaluateVisibility();
  });

  return { visible: enabled ? visible : true, distance: distanceRef.current };
}
